import { useRef } from 'react'

import { Box, Button, Checkbox } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { EmployeeForSelectFragment, ModalTaskFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import DropZone from 'modules/core/components/DropZone'
import FormRow from 'modules/core/components/FormRow'
import LabeledInput from 'modules/core/components/LabeledInput'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { ACCEPTED_CONTENT_TYPES, acceptedContentTypes } from 'modules/core/lib/fileFormats'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import FileList from 'modules/tasks/components/FileList'
import { useFormTaskFiles } from 'modules/tasks/hooks/useFormTaskFiles'
import { useValidateStringLength } from 'modules/tasks/hooks/useValidateStringLength'
import { TasksFiles } from 'modules/tasks/store/collections/tasks_files'
import type TaskFile from 'modules/tasks/store/models/TaskFile'

export interface ModalTaskWithFiles extends ModalTaskFragment {
  removeFiles: TasksFiles
  newFiles: TasksFiles
  createTaskPerAsignee?: boolean
}

export const acceptedContentTypesByTasks = () => {
  const contentTypes = ACCEPTED_CONTENT_TYPES.filter((contentType: string): boolean => {
    const forbiddenTypes = ['zip', 'html']

    return !forbiddenTypes.some((type: string) => contentType.includes(type))
  })
  return acceptedContentTypes(contentTypes)
}

const TASK_FORM_MODE = {
  new: 'new',
  edit: 'edit',
}

interface Props {
  mode: 'new' | 'edit'
  submitting: boolean
  loading?: boolean
  readonly?: boolean
  form: FormikProps<ModalTaskWithFiles>
  close: () => void
  onSubmit: (removeFiles: TasksFiles, newFiles: TasksFiles) => void
  files: TaskFile[]
}

const TaskForm = ({
  mode,
  submitting,
  readonly,
  form,
  close,
  onSubmit,
  files,
  loading = false,
}: Props) => {
  const editorRef = useRef<EditorHandle>(null)
  const { newFiles, removeFiles, onDropFiles, onDeleteNewFile, onDeleteExistingFile } =
    useFormTaskFiles()

  const { validate, isValid } = useValidateStringLength()
  useUnsafeEffect(() => {
    validate(form.values.name)
  }, [form.values.name, validate])

  const onSelectAssignees = (employees: EmployeeForSelectFragment[]) => {
    form.setFieldValue('assignees', {
      nodes: employees.map((employee) => ({ employeeName: employee })),
    })
  }

  const defaultAssigneeIds = form.values.assignees.nodes

  const selectedAssignees: EmployeeForSelectFragment[] = defaultAssigneeIds
    .map((assignee) => assignee.employeeName)
    .filter(Boolean)

  const filesCount = files.length + newFiles.length
  const taskIsNew = mode === TASK_FORM_MODE.new
  const taskTitle = taskIsNew ? i18n.t('tasks.form.new.title') : i18n.t('tasks.form.edit.title')
  const formButtonTitle = taskIsNew
    ? i18n.t('tasks.form.new.submit')
    : i18n.t('tasks.form.edit.submit')
  const descriptionParams = { user: form.values.author?.fullName }
  const taskDescription = taskIsNew
    ? i18n.t('tasks.form.new.description', descriptionParams)
    : i18n.t('tasks.form.edit.description', descriptionParams)

  return (
    <Modal
      illustration="check"
      title={taskTitle}
      description={taskDescription}
      onClose={close}
      onSubmit={() => onSubmit(removeFiles, newFiles)}
      footer={
        <Button disabled={!isValid || submitting || readonly} submit>
          {formButtonTitle}
        </Button>
      }
    >
      {loading && <Loading />}
      {!loading && (
        <Box width="s400">
          <FormRow>
            <TextField
              label={i18n.t('tasks.labels.name')}
              value={form.values.name}
              onChange={(value) => form.setFieldValue('name', value)}
              readonly={readonly}
              mandatory
            />
          </FormRow>
          <FormRow>
            <LabeledInput isLegend label={i18n.t('tasks.labels.content')}>
              <TextEditor
                ref={editorRef}
                initialEditorState={{ data: (form.values.content || '').toString() ?? 'fds' }}
                placeholder={i18n.t(
                  'processes.process_settings.tasks.form.instruction_placeholder'
                )}
                height="s200"
                onChange={(e) => {
                  form.setFieldValue('content', e)
                }}
                enableBetaFeatures={false}
              />
            </LabeledInput>
          </FormRow>
          <FormRow>
            <EmployeeSelector
              width="s400"
              label={i18n.t('tasks.labels.assignees')}
              emptyLabel={i18n.t('tasks.labels.assignees')}
              multiple
              value={selectedAssignees}
              excludeCurrentEmployee={false}
              withAvatar
              onSelect={onSelectAssignees}
              usingAccessIds
            />
            {taskIsNew && (
              <Box flexDirection="row" justifyContent="spaceBetween" paddingTop="s8">
                <Checkbox
                  size="small"
                  label={i18n.t('tasks.labels.mandatory_for_all_assignees')}
                  checked={form.values.createTaskPerAsignee}
                  onChange={(event) =>
                    form.setFieldValue('createTaskPerAsignee', event.target.checked)
                  }
                />
              </Box>
            )}
          </FormRow>
          <FormRow>
            <DayPicker
              label={i18n.t('tasks.labels.due_date')}
              value={form.values.dueOn || ''}
              readonly={!!readonly}
              onChange={(date) => {
                form.setFieldValue('dueOn', date)
              }}
              placeholder="–"
              mode="single"
              valueFormat="backend"
            />
          </FormRow>
          <FormRow>
            {!readonly && <DropZone accept={acceptedContentTypesByTasks()} onDrop={onDropFiles} />}
            {filesCount > 0 && (
              <Box paddingTop="s24">
                <FileList
                  readonly={readonly}
                  currentFiles={files}
                  newFiles={newFiles.toArray()}
                  onDeleteExistingFile={onDeleteExistingFile}
                  onDeleteNewFile={onDeleteNewFile}
                />
              </Box>
            )}
          </FormRow>
        </Box>
      )}
    </Modal>
  )
}

export default observer(TaskForm)
